'use client';

import { ErrorSection } from '@pickleballinc/features/src/error-pages/error-section';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import { useRTE } from '@pickleballinc/ui/components/global/RTEProvider';
import { getMatchesInSnakeCase } from '@pickleballinc/ui/lib/rte-helpers';
import type { RTEMatchInfoForResults } from '@pickleballinc/ui/types/rte';
import clsx from 'clsx';
import { notFound } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import {
  useGetListLogs,
  useGetMatchesH2H,
  useGetMatchesStats,
  useGetResultMatchInfos,
} from '@/hooks/matches/hooks';
import { useGetTournamentEvent } from '@/hooks/results/hooks';
import { publicEnv } from '@/lib/env';
import MatchCardModalWrapper from '@/modules/results/components/match/MatchCardModalWrapper';
import { MatchDetailsSkeleton } from '@/modules/results/list/MatchDetailsSkeleton';
import { MatchResultsCardSkeleton } from '@/modules/results/list/MatchResultsCardSkeleton';
import MatchH2H from '@/modules/results/match/MatchH2H';
import MatchLogsLive from '@/modules/results/match/MatchLogsLive';
import { MatchStats } from '@/modules/results/match/MatchStats';
import { ResultMatchCard } from '@/modules/results/match/ResultMatchCard';
import { Log, Match, MatchTabOption } from '@/modules/results/types';
import { getBracketsAppRedirectURL } from '@/utils/helpers/getBracketsAppRedirectURL';

interface MatchDetailsProps {
  id: string;
}

interface Player {
  name: string;
  uuid: string;
  teamUuid: string;
}

const PPA_Club_Id = '80f17003-cd92-4fc4-84e4-06d38e096042';
const UPA_Club_Id = 'a484ec40-ff2f-4a27-a831-25ac6408b64a';

// `/results/match/<id>` and `/results/matchup/<id>` are both in AD_FREE_PAGES, and these
// components render on nothing else, so the leaderboard slot was removed outright rather
// than gated. Re-adding one means taking the path back out of that list.
export const MatchDetails = ({ id }: MatchDetailsProps) => {
  const [isRefereeApp, setIsRefereeApp] = useState(false);
  const [activeTab, setActiveTab] = useState<MatchTabOption>('stats');
  const [playerList, setPlayerList] = useState<Player[]>([]);
  const [playersForHistoryModal, setPlayersForHistoryModal] = useState<Player[]>([]);
  const [userChangedTab, setUserChangedTab] = useState(false);
  const [openMatchUUID, setOpenMatchUUID] = useState<string>('');
  const [PPAOnly, setPPAOnly] = useState(true);
  const isRTEENabled = publicEnv.NEXT_PUBLIC_RTE_ENABLED === true;
  const [shouldRefetch, setShouldRefetch] = useState(!isRTEENabled);
  const [matchStarted, setMatchStarted] = useState(false);

  const { data: matchInfoResult, isLoading: isLoadingMatchInfo } = useGetResultMatchInfos(
    id,
    shouldRefetch
  );

  const matchInfoData = matchInfoResult?.data ?? [];
  const matchInfoError = matchInfoResult?.error;

  const isMatchLive = !isLoadingMatchInfo && matchInfoData[0]?.match_status === 2;

  useEffect(() => {
    if (!isRTEENabled && isMatchLive) {
      setShouldRefetch(true);
    }
  }, [isMatchLive]);

  const { data: matchStatsResults, isLoading: isLoadingMatchStatsResults } = useGetMatchesStats(
    id,
    isMatchLive || matchStarted
  );
  const matchStatsData = matchStatsResults?.data;
  const matchStatsError = matchStatsResults?.error;

  const { data: matchListLogsResults } = useGetListLogs(id, shouldRefetch);
  const matchListLogsData = matchListLogsResults?.data;
  const matchListLogsError = matchListLogsResults?.error;

  const { data: matchH2HResults, isLoading: isLoadingMatchH2H } = useGetMatchesH2H(id);
  const matchH2HData = matchH2HResults?.data;
  const matchH2HError = matchH2HResults?.error;

  const isProEvent =
    matchInfoData[0]?.bracket_level_title === 'Pro' &&
    (matchInfoData[0].club_uuid === PPA_Club_Id || matchInfoData[0].club_uuid === UPA_Club_Id);

  useEffect(() => {
    // Check if matchStatsData is available and has MatchInfo
    if (matchStatsData && matchStatsData.MatchInfo) {
      // Extract the player names and UUIDs
      const players = [];
      if (matchStatsData.MatchInfo.team_one_player_one_name) {
        players.push({
          name: matchStatsData.MatchInfo.team_one_player_one_name,
          uuid: matchStatsData.MatchInfo.team_one_player_one_uuid,
          teamUuid: matchStatsData.MatchInfo.team_one_uuid,
        });
      }
      if (matchStatsData.MatchInfo.team_one_player_two_name) {
        players.push({
          name: matchStatsData.MatchInfo.team_one_player_two_name,
          uuid: matchStatsData.MatchInfo.team_one_player_two_uuid,
          teamUuid: matchStatsData.MatchInfo.team_one_uuid,
        });
      }
      if (matchStatsData.MatchInfo.team_two_player_one_name) {
        players.push({
          name: matchStatsData.MatchInfo.team_two_player_one_name,
          uuid: matchStatsData.MatchInfo.team_two_player_one_uuid,
          teamUuid: matchStatsData.MatchInfo.team_two_uuid,
        });
      }
      if (matchStatsData.MatchInfo.team_two_player_two_name) {
        players.push({
          name: matchStatsData.MatchInfo.team_two_player_two_name,
          uuid: matchStatsData.MatchInfo.team_two_player_two_uuid,
          teamUuid: matchStatsData.MatchInfo.team_two_uuid,
        });
      }
      setPlayerList(players);
    }
  }, [matchStatsData]);

  useEffect(() => {
    const firstMatchInfo = matchInfoData[0];
    if (matchInfoData && firstMatchInfo) {
      const playersForModal = [];
      if (firstMatchInfo.team_one_player_one_name) {
        playersForModal.push({
          name: firstMatchInfo.team_one_player_one_name,
          uuid: firstMatchInfo.team_one_player_one_uuid,
          teamUuid: firstMatchInfo.team_one_uuid,
        });
      }
      if (firstMatchInfo.team_one_player_two_name) {
        playersForModal.push({
          name: firstMatchInfo.team_one_player_two_name,
          uuid: firstMatchInfo.team_one_player_two_uuid,
          teamUuid: firstMatchInfo.team_one_uuid,
        });
      }
      if (firstMatchInfo.team_two_player_one_name) {
        playersForModal.push({
          name: firstMatchInfo.team_two_player_one_name,
          uuid: firstMatchInfo.team_two_player_one_uuid,
          teamUuid: firstMatchInfo.team_two_uuid,
        });
      }
      if (firstMatchInfo.team_two_player_two_name) {
        playersForModal.push({
          name: firstMatchInfo.team_two_player_two_name,
          uuid: firstMatchInfo.team_two_player_two_uuid,
          teamUuid: firstMatchInfo.team_two_uuid,
        });
      }
      setPlayersForHistoryModal(playersForModal);
    }
  }, [matchInfoData, matchStatsData]);

  const renderContent = () => {
    if (activeTab === 'stats') {
      const isStatsInitialLoading = !matchStatsResults && isLoadingMatchStatsResults;
      if (isStatsInitialLoading || isLoadingMatchInfo) {
        return <MatchDetailsSkeleton />;
      }
      if (!isRefereeApp) return null;

      if (matchStatsError) {
        return (
          <div className='pb-12'>
            <ErrorSection title={matchStatsError} />
          </div>
        );
      }

      if (!matchStatsData || !matchStatsData.MatchInfo) {
        return (
          <div className='pb-12'>
            <NoDataFound title='Data is currently unavailable. Please try again later.' />
          </div>
        );
      }

      return <MatchStats matchStats={matchStatsData} playerList={playerList} />;
    }
    if (activeTab === 'h2h') {
      const isH2HInitialLoading = !matchH2HResults && isLoadingMatchH2H;
      if (isH2HInitialLoading) {
        return <MatchDetailsSkeleton />;
      }

      if (matchH2HError) {
        return (
          <div className='pb-12'>
            <ErrorSection title={matchH2HError} />
          </div>
        );
      }

      if (!matchH2HData) {
        return (
          <div className='pb-12'>
            <NoDataFound title='Data is currently unavailable. Please try again later.' />
          </div>
        );
      }

      const h2hDataToUse = isProEvent && PPAOnly ? matchH2HData.ppa_upa_only : matchH2HData;
      const matchInfo = matchStatsData?.MatchInfo;

      return (
        <MatchH2H
          matchH2HData={h2hDataToUse}
          blt={matchInfoData[0]?.bracket_level_title}
          formatTitle={matchInfoData[0]?.format_title || ''}
          playerList={playersForHistoryModal}
          date={matchInfo?.match_start || ''}
          datePlaned={
            matchInfoData[0]?.match_planned_start ||
            matchInfoData[0]?.match_start ||
            matchInfoData[0]?.match_assigned_to_court ||
            ''
          }
          matchStatus={matchInfo?.match_status || 0}
          PPAOnly={PPAOnly}
          setPPAOnly={setPPAOnly}
          isProEvent={isProEvent}
          pbracketsURL={pbracketsURL}
        />
      );
    }
    return null;
  };

  const onTabClick = (tabValue: MatchTabOption) => {
    setActiveTab(tabValue);
    setUserChangedTab(true);
  };

  const handleOnKeyDownTab =
    (tabValue: MatchTabOption) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onTabClick(tabValue);
      }
    };

  const [matchesData, setMatchesData] = useState<Match[]>([]);

  useEffect(() => {
    if (matchInfoData.length > 0) {
      setMatchesData(matchInfoData);
    }
  }, [matchInfoData]);

  const { matches } = useRTE();

  useMemo(() => {
    if (isRTEENabled && matchesData.length > 0 && matches.length > 0) {
      const matchesSnakeCase = getMatchesInSnakeCase(matches);

      setMatchesData((matchesPrev) => {
        const matchesDataCopy = [...matchesPrev];

        matchesSnakeCase.forEach((newMatch) => {
          const matchIndex = matchesPrev.findIndex(
            (match) => match.match_uuid === newMatch.match_uuid
          );

          if (matchIndex !== -1) {
            const matchToUpdate = matchesDataCopy[matchIndex];

            if (matchToUpdate) {
              // Check if at least one field in newMatch is different from matchToUpdate
              const hasDifferences = (
                Object.keys(newMatch) as (keyof RTEMatchInfoForResults)[]
              ).some((key) => newMatch[key] !== matchToUpdate[key as keyof Match]);

              // Create a new object by removing any `undefined` values from `newMatch` and adding `changeHappened` only if there are differences
              const filteredNewMatch = Object.fromEntries(
                Object.entries({ ...newMatch, change_happened: hasDifferences }).filter(
                  ([_, value]) => value !== undefined
                )
              );

              matchesDataCopy[matchIndex] = { ...matchToUpdate, ...filteredNewMatch } as Match;
            }
          }
        });

        return [...matchesDataCopy];
      });
    }
  }, [matches]);

  useEffect(() => {
    if (matchesData[0]) {
      const isRefApp = matchesData[0].is_consumed_by_referee_app || false;

      setIsRefereeApp(isRefApp);

      if (!userChangedTab) {
        setActiveTab(isRefApp ? 'stats' : 'h2h');
      }

      if (matchesData[0].match_status === 2) {
        setMatchStarted(true);
      }
    }
  }, [matchesData]);

  useEffect(() => {
    if (matchesData[0]) {
      const isRefApp = matchesData[0].is_consumed_by_referee_app || false;

      setIsRefereeApp(isRefApp);

      if (!userChangedTab) {
        setActiveTab(isRefApp ? 'stats' : 'h2h');
      }
    }
  }, [userChangedTab]);

  // getting necessary tournament event data - needed for correctly linking to brackets
  const { data: tournamentEventResults } = useGetTournamentEvent(
    matchesData[0]?.tournament_uuid || '',
    matchesData[0]?.event_uuid || ''
  );

  const tournamentEventData = tournamentEventResults?.data;

  const pbracketsURL = getBracketsAppRedirectURL(tournamentEventData);

  const isDataLoading = isLoadingMatchInfo || isLoadingMatchH2H;

  // The route handler 404s private games, so there is nothing left to inspect here.
  if (matchInfoResult?.isNotFound) {
    notFound();
  }

  return (
    <>
      <div className='relative mt-4 rounded-t-lg sm:mt-6 sm:rounded-t-xl'>
        {isDataLoading ? (
          <MatchResultsCardSkeleton />
        ) : matchInfoError ? (
          <div
            style={{
              backgroundImage: `url(${getCDNURL(`${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/match-stats.svg`, 1000, 1000)})`,
              backgroundPosition: 'top',
              backgroundSize: 'cover',
            }}
            className='flex min-h-[416px] w-full 
                      flex-col items-center px-2 sm:min-h-[484px] sm:justify-center sm:px-4'
          >
            <ErrorSection title={matchInfoError} titleClassName='text-white' />
          </div>
        ) : !matchesData.length || !matchesData[0] ? (
          <NoDataFound title='No matches available' />
        ) : (
          <>
            <ResultMatchCard
              match={matchesData[0]}
              matchH2HData={matchH2HData}
              setOpenMatchUUID={setOpenMatchUUID}
              pbracketsURL={pbracketsURL}
            />

            {isMatchLive && matchesData[0]?.live_url ? (
              <div className='flex h-[60px] items-center justify-center bg-white'>
                <a
                  href={matchesData[0].live_url}
                  target='_blank'
                  aria-label='Link to a match live stream (opens in a new tab)'
                  rel='noreferrer'
                >
                  <button className='flex h-[36px] w-[109px] items-center justify-center rounded-lg border border-gray-200 shadow-xs'>
                    <div className='mr-2 size-2 rounded-full bg-darkorange-600' />
                    <p className='text-xs font-semibold text-gray-900'>Watch Now</p>
                  </button>
                </a>
              </div>
            ) : null}
            <div className='flex justify-center gap-5 bg-black text-white'>
              {isRefereeApp ? (
                <div
                  role='tab'
                  tabIndex={0}
                  onClick={() => onTabClick('stats')}
                  onKeyDown={() => handleOnKeyDownTab('stats')}
                  className={clsx(
                    'mb-1 cursor-pointer border-b-4 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
                    activeTab === 'stats' ? 'border-b-white' : 'border-b-transparent'
                  )}
                >
                  Match Stats
                </div>
              ) : null}
              <div
                role='tab'
                tabIndex={0}
                onClick={() => onTabClick('h2h')}
                onKeyDown={() => handleOnKeyDownTab('h2h')}
                className={clsx(
                  'mb-1 cursor-pointer border-b-4  p-4',
                  activeTab === 'h2h' ? 'border-b-white' : 'border-b-transparent'
                )}
              >
                Head to Head
              </div>
            </div>
          </>
        )}
        <div className='container relative mx-auto py-3 sm:py-4 lg:px-4 2xl:px-0'>
          <div className='mt-10 flex w-full justify-center gap-5'>{renderContent()}</div>
        </div>
        {activeTab === 'stats' && isRefereeApp && matchStatsData && matchesData[0]?.match_uuid ? (
          matchListLogsError ? (
            <ErrorSection title={matchListLogsError} />
          ) : (
            <MatchLogsLive
              matchUuid={matchesData[0].match_uuid}
              isMatchLive={matchStarted || isMatchLive}
              initialLogs={matchListLogsData as unknown as Log[]}
              playerList={playerList}
              utcOffset={matchStatsData?.MatchInfo?.utc_offset}
              timezoneAbbreviation={matchStatsData?.MatchInfo?.timezone_abbreviation}
            />
          )
        ) : null}
      </div>
      {matchesData[0] && openMatchUUID !== '' ? (
        <MatchCardModalWrapper matches={matchesData[0]} setOpenMatchUUID={setOpenMatchUUID} />
      ) : null}
    </>
  );
};
